import { useState } from "react";
import {
  PAGE_ACTION_DEPENDENCIES,
  SUPER_TENANT_ONLY_ACTION_ROUTES,
  type HttpMethod,
} from "@nexus/contracts";
import { ALL_PERM_DEFS, type PermDef } from "@/features/identity-hub/lib/permission-catalog";

export interface PermissionPayloadItem {
  type: PermDef["type"];
  /** Every verb the API accepts, not just the ones the catalog happens to use. */
  method: HttpMethod;
  route: string;
}

/** Stable key for deduping permission tuples (DB unique is accessId+type+method+route). */
export function permissionTupleKey(perm: { type: string; method: string; route: string }): string {
  return `${perm.type}|${perm.method}|${perm.route}`;
}

/** Catalog keys that map to the same stored permission tuple (e.g. shared `/uploads/*` routes). */
const CATALOG_KEYS_BY_TUPLE = (() => {
  const map = new Map<string, string[]>();
  for (const def of ALL_PERM_DEFS) {
    const tupleKey = permissionTupleKey(def);
    const keys = map.get(tupleKey) ?? [];
    keys.push(def.key);
    map.set(tupleKey, keys);
  }
  return map;
})();

function aliasCatalogKeys(key: string): string[] {
  const def = ALL_PERM_DEFS.find((d) => d.key === key);
  if (!def) return [key];
  return CATALOG_KEYS_BY_TUPLE.get(permissionTupleKey(def)) ?? [key];
}

/** Selected permission keys -> the `{ type, method, route }` payload the API expects. */
export function permissionKeysToPayload(keys: ReadonlySet<string>): PermissionPayloadItem[] {
  const byTuple = new Map<string, PermissionPayloadItem>();
  for (const def of ALL_PERM_DEFS) {
    if (!keys.has(def.key)) continue;
    const tupleKey = permissionTupleKey(def);
    if (!byTuple.has(tupleKey)) {
      byTuple.set(tupleKey, { type: def.type, method: def.method, route: def.route });
    }
  }
  return Array.from(byTuple.values());
}

const PAYLOAD_METHODS: readonly string[] = ["GET", "POST", "PUT", "PATCH", "DELETE", "OPTIONS", "HEAD"];

function isPayloadMethod(method: string): method is PermissionPayloadItem["method"] {
  return PAYLOAD_METHODS.includes(method);
}

function isPermissionType(type: string): type is PermissionPayloadItem["type"] {
  return type === "PAGE" || type === "ACTION";
}

function isEditable(keys: string[] | undefined, grantableKeys?: ReadonlySet<string> | null): boolean {
  if (!keys || keys.length === 0) return false;
  if (!grantableKeys) return true;
  return keys.some((key) => grantableKeys.has(key));
}

/**
 * Stored permission tuples the editor cannot offer, kept as-is on save: not in
 * the IAM catalog, or outside `grantableKeys` because the viewer does not hold
 * them. Editing a role must never silently revoke what it does not show.
 */
export function unmatchedPermissionTuples(
  permissions: Array<{ type: string; method: string; route: string }>,
  grantableKeys?: ReadonlySet<string> | null,
): PermissionPayloadItem[] {
  const out: PermissionPayloadItem[] = [];
  for (const perm of permissions) {
    if (!isPermissionType(perm.type) || !isPayloadMethod(perm.method)) continue;
    if (isEditable(CATALOG_KEYS_BY_TUPLE.get(permissionTupleKey(perm)), grantableKeys)) continue;
    out.push({ type: perm.type, method: perm.method, route: perm.route });
  }
  return out;
}

/**
 * A role's granted `{ type, method, route }` permissions -> catalog keys, for
 * seeding selection state. Pass `grantableKeys` to keep the selection to what
 * the viewer may edit; the rest belongs in `unmatchedPermissionTuples`.
 */
export function permissionPayloadToKeys(
  permissions: Array<{ type: string; method: string; route: string }>,
  grantableKeys?: ReadonlySet<string> | null,
): Set<string> {
  const keys = new Set<string>();
  for (const perm of permissions) {
    for (const key of CATALOG_KEYS_BY_TUPLE.get(permissionTupleKey(perm)) ?? []) {
      if (grantableKeys && !grantableKeys.has(key)) continue;
      keys.add(key);
    }
  }
  return keys;
}

const PLATFORM_ONLY_CATALOG_KEYS: ReadonlySet<string> = new Set(
  ALL_PERM_DEFS
    .filter((def) => SUPER_TENANT_ONLY_ACTION_ROUTES.has(def.route))
    .map((def) => def.key),
);

export function isPlatformOnlyCatalogKey(key: string): boolean {
  return PLATFORM_ONLY_CATALOG_KEYS.has(key);
}

/**
 * Catalog keys already on the role that the viewer cannot revoke (they do not
 * hold them). Shown as locked-on so Save cannot silently 403 on a grantable delta.
 */
export function lockedCatalogKeys(
  permissions: Array<{ type: string; method: string; route: string }>,
  grantableKeys?: ReadonlySet<string> | null,
): Set<string> {
  if (!grantableKeys) return new Set();
  const keys = new Set<string>();
  for (const perm of permissions) {
    const catalogKeys = CATALOG_KEYS_BY_TUPLE.get(permissionTupleKey(perm)) ?? [];
    if (catalogKeys.length === 0) continue;
    if (catalogKeys.some((key) => grantableKeys.has(key))) continue;
    for (const key of catalogKeys) keys.add(key);
  }
  return keys;
}

const PAGE_CATALOG_KEYS_BY_ROUTE: ReadonlyMap<string, string[]> = (() => {
  const map = new Map<string, string[]>();
  for (const def of ALL_PERM_DEFS) {
    if (def.type !== "PAGE") continue;
    const keys = map.get(def.route) ?? [];
    keys.push(def.key);
    map.set(def.route, keys);
  }
  return map;
})();

const OWNING_PAGE_KEYS_BY_ACTION_KEY: ReadonlyMap<string, string[]> = (() => {
  const map = new Map<string, string[]>();
  for (const [pageRoute, actionRoutes] of Object.entries(PAGE_ACTION_DEPENDENCIES)) {
    const pageKeys = PAGE_CATALOG_KEYS_BY_ROUTE.get(pageRoute) ?? [];
    if (pageKeys.length === 0) continue;
    const actionRouteSet = new Set(actionRoutes);
    for (const def of ALL_PERM_DEFS) {
      if (def.type !== "ACTION" || !actionRouteSet.has(def.route)) continue;
      const owners = map.get(def.key) ?? [];
      for (const pageKey of pageKeys) {
        if (!owners.includes(pageKey)) owners.push(pageKey);
      }
      map.set(def.key, owners);
    }
  }
  return map;
})();

/**
 * Page catalog keys that must stay selected because they are the last owner of
 * a locked action. Removing them would 400 on page-action dependencies.
 */
export function impliedLockedCatalogKeys(
  lockedKeys: ReadonlySet<string>,
  selected: ReadonlySet<string>,
): Set<string> {
  if (lockedKeys.size === 0) return new Set();
  const granted = new Set(selected);
  for (const key of lockedKeys) granted.add(key);
  const implied = new Set<string>();
  for (const actionKey of lockedKeys) {
    const owners = OWNING_PAGE_KEYS_BY_ACTION_KEY.get(actionKey) ?? [];
    const present = owners.filter((key) => granted.has(key));
    if (present.length === 1) implied.add(present[0]!);
  }
  return implied;
}

export function effectiveLockedCatalogKeys(
  lockedKeys: ReadonlySet<string> | undefined,
  selected: ReadonlySet<string>,
): Set<string> {
  const locked = new Set(lockedKeys ?? []);
  for (const key of impliedLockedCatalogKeys(locked, selected)) locked.add(key);
  return locked;
}

/** Re-split a draft into editable keys vs preserved tuples when grantability changes. */
export function repartitionPermissionDraft(
  selected: ReadonlySet<string>,
  preserved: readonly PermissionPayloadItem[],
  grantableKeys?: ReadonlySet<string> | null,
): { selected: Set<string>; preserved: PermissionPayloadItem[] } {
  const payload = [...permissionKeysToPayload(selected), ...preserved];
  return {
    selected: permissionPayloadToKeys(payload, grantableKeys),
    preserved: unmatchedPermissionTuples(payload, grantableKeys),
  };
}

interface UsePermissionSelectionOptions {
  /** Fired on every toggle/group/remove — e.g. to clear an active preset selection. */
  onChange?: () => void;
}

/**
 * Selection state for the permission catalog checkboxes, shared by NewRoleDialog
 * and TemplateDialog: toggle a single permission, toggle a whole action
 * group, or bulk-remove keys (used when revoking page access cascades to its
 * action permissions).
 */
export function usePermissionSelection(initial: Set<string> = new Set(), { onChange }: UsePermissionSelectionOptions = {}) {
  const [selected, setSelected] = useState<Set<string>>(initial);

  const togglePerm = (key: string) => {
    onChange?.();
    const aliases = aliasCatalogKeys(key);
    setSelected((prev) => {
      const next = new Set(prev);
      const turningOn = !prev.has(key);
      for (const alias of aliases) {
        if (turningOn) next.add(alias);
        else next.delete(alias);
      }
      return next;
    });
  };

  const toggleGroup = (keys: string[]) => {
    onChange?.();
    const allSelected = keys.every((k) => selected.has(k));
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) keys.forEach((k) => next.delete(k));
      else keys.forEach((k) => next.add(k));
      return next;
    });
  };

  const removeKeys = (keys: string[]) => {
    onChange?.();
    setSelected((prev) => {
      const next = new Set(prev);
      for (const k of keys) next.delete(k);
      return next;
    });
  };

  return { selected, setSelected, togglePerm, toggleGroup, removeKeys, selectedCount: selected.size };
}
